import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IConnectorTypeDTO } from 'data/dtos/automation/i-connector-type-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'

import { ajvResolver } from '@hookform/resolvers/ajv'
import ConnectorTypeSchema, { IConnectorTypeFormData } from 'schema/automation/connector-type'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const ConnectorTypeForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IConnectorTypeFormData>({
    resolver: ajvResolver(ConnectorTypeSchema),
    defaultValues: {
      connectorTypeName: '',
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/connector-types/${id}`)
        .then((response) => {
          const { data } = response.data

          const connectorTypeResult = {
            connectorTypeName: data.connectorTypeName,
            isInactive: data.isInactive,
          }

          return connectorTypeResult
        })
        .then((connectorTypeResult: IConnectorTypeDTO) => {
          reset(connectorTypeResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: IConnectorTypeDTO) => {
    setIsLoading(true)

    const payLoad: IConnectorTypeDTO = {
      connectorTypeName: data.connectorTypeName,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/connector-types`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/connector-types')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/connector-types', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Tipos de Conectores'
        subTitle='Dados sobre os tipos de conectores.'
      />
      <Form.Body>
        <Form.Input
          label='Nome do Tipo de Conector'
          error={!!errors.connectorTypeName}
          errorMessage={errors?.connectorTypeName?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          maxLength={100}
          name='connectorTypeName'
          rules={{ onChange: (e) => handleChange(e) }}
          register={register}
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default ConnectorTypeForm
